import React, { useEffect, useRef, useState } from 'react';
import { setAudioConsent, AudioChoice } from '../utils/audioConsent';
import { fireAnalyticsEvent } from '../utils/analytics';
import { assetUrl } from '../utils/assets';
import './AudioConsentPage.css';

interface AudioConsentPageProps {
  onChoiceMade: () => void;
}

const HEAR_IMG = assetUrl('/assets/ui/narration_on.png');
const READ_IMG = assetUrl('/assets/ui/narration_off.png');

const AudioConsentPage: React.FC<AudioConsentPageProps> = ({ onChoiceMade }) => {
  const [exiting, setExiting] = useState(false);
  const [selected, setSelected] = useState<AudioChoice | null>(null);
  const shownAtRef = useRef<number>(Date.now());

  useEffect(() => {
    fireAnalyticsEvent('audio_modal_shown');
    shownAtRef.current = Date.now();
  }, []);

  const handleChoice = (choice: AudioChoice) => {
    if (selected) return;
    const timeToDecideMs = Date.now() - shownAtRef.current;
    setSelected(choice);
    setAudioConsent(choice);
    fireAnalyticsEvent('audio_modal_choice', { choice, time_to_decide_ms: timeToDecideMs });
    // Brief confirmation pulse on the chosen tile, then fade the page out.
    setTimeout(() => setExiting(true), 180);
    setTimeout(onChoiceMade, 480);
  };

  const tileClass = (choice: AudioChoice) =>
    `audio-consent-tile${selected === choice ? ' selected' : ''}`;

  return (
    <div className={`audio-consent-page ${exiting ? 'exiting' : ''}`}>
      <div className="audio-consent-content">
        <h1 className="audio-consent-headline">Want narration?</h1>
        <p className="audio-consent-subtitle">You can change this anytime.</p>

        <div className="audio-consent-tiles">
          <button
            className={tileClass('hear')}
            onClick={() => handleChoice('hear')}
            aria-label="Hear it — enable narration and audio"
          >
            <img src={HEAR_IMG} alt="" className="audio-consent-image" />
            <span className="audio-consent-label">Hear it</span>
          </button>

          <button
            className={tileClass('read')}
            onClick={() => handleChoice('read')}
            aria-label="Read it — silent, no audio"
          >
            <img src={READ_IMG} alt="" className="audio-consent-image" />
            <span className="audio-consent-label">Read it</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudioConsentPage;
